import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

let ffmpegPromise = null;

const LOCAL_BASE = `${window.location.origin}/ffmpeg/`;
const CDN_BASE   = `https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd/`;


function isAndroid() {
  const ua = (navigator.userAgent || "").toLowerCase();
  const plat = (navigator.userAgentData?.platform || navigator.platform || "").toLowerCase();
  return ua.includes("android") || plat.includes("android");
}

function isIOS() {
  const ua = navigator.userAgent || "";
  const plat = navigator.platform || "";
  const iOSLike = /iPhone|iPad|iPod/.test(ua) || /iPhone|iPad|iPod/.test(plat);
  const iPadAsMac = /Mac/.test(plat) && "ontouchend" in window;
  return iOSLike || iPadAsMac;
}


async function ping(url) {
  try {
    const r = await fetch(url, { method: "HEAD", cache: "no-cache" });
    return r.ok;
  } catch { return false; }
}

async function loadWith(base, onProgress) {
  const ff = new FFmpeg();
  if (onProgress) ff.on("progress", onProgress);
  await ff.load({
    coreURL:   `${base}ffmpeg-core.js`,
    wasmURL:   `${base}ffmpeg-core.wasm`,
    workerURL: `${base}ffmpeg-core.worker.js`,
  });
  return ff;
}

async function getFFmpeg(onProgress) {
  if (ffmpegPromise) return ffmpegPromise;

  const localOk =
    await ping(`${LOCAL_BASE}ffmpeg-core.js`) &&
    await ping(`${LOCAL_BASE}ffmpeg-core.wasm`) &&
    await ping(`${LOCAL_BASE}ffmpeg-core.worker.js`);

  ffmpegPromise = localOk
    ? loadWith(LOCAL_BASE, onProgress).catch((e) => {
        console.warn("[ffmpeg] local load failed, using CDN:", e);
        return loadWith(CDN_BASE, onProgress);
      })
    : loadWith(CDN_BASE, onProgress);

  return ffmpegPromise;
}


function sniffExt(mimeType = "", filename = "") {
  const m = (mimeType || "").toLowerCase();
  const f = (filename || "").toLowerCase();
  if (m.includes("mp4") || f.endsWith(".mp4")) return "mp4";
  if (m.includes("webm") || f.endsWith(".webm")) return "webm";
  return "";
}

async function sniffContainerFromBytes(blob) {
  try {
    const head = await blob.slice(0, 32).arrayBuffer();
    const u = new Uint8Array(head);

    // WebM EBML header
    if (u.length >= 4 && u[0] === 0x1A && u[1] === 0x45 && u[2] === 0xDF && u[3] === 0xA3) return "webm";

    // MP4 'ftyp'
    if (u.length >= 8 && u[4] === 0x66 && u[5] === 0x74 && u[6] === 0x79 && u[7] === 0x70) return "mp4";

    // some mp4 variants have ftyp at different offsets
    for (let i = 0; i <= 16 && i + 3 < u.length; i++) {
      if (u[i] === 0x66 && u[i+1] === 0x74 && u[i+2] === 0x79 && u[i+3] === 0x70) return "mp4";
    }
  } catch {}
  return "";
}


export async function convertWebMToMp4(
  webmBlob,
  { speedProfile = "veryfast", crf, maxWidth, fps, onProgress } = {}
) {
  const ff = await getFFmpeg(onProgress);
  const inName = "input.webm";
  const outName = "output.mp4";

  await ff.writeFile(inName, await fetchFile(webmBlob));

  const preset =
    speedProfile === "ultrafast" ? "ultrafast" :
    speedProfile === "medium"    ? "medium"    : "veryfast";

  const crfVal = typeof crf === "number" ? String(crf)
                : speedProfile === "ultrafast" ? "28" : "23";

  const vf = [];
  if (typeof maxWidth === "number" && maxWidth > 0) {
    vf.push(`scale=min(${maxWidth}\\,iw):-2`);
  }
  if (fps) vf.push(`fps=${fps}`);
  const vfArgs = vf.length ? ["-vf", vf.join(",")] : [];

  try {
    await ff.exec([
      "-i", inName,
      ...vfArgs,
      "-c:v", "libx264",
      "-preset", preset,
      "-crf", crfVal,
      "-pix_fmt", "yuv420p",
      "-movflags", "+faststart",
      "-c:a", "aac",
      "-b:a", "128k",
      outName,
    ]);
  } catch (e) {
    console.warn("[ffmpeg] x264/aac failed, fallback to mpeg4/mp2:", e);
    await ff.exec([
      "-i", inName,
      ...vfArgs,
      "-c:v", "mpeg4",
      "-q:v", "3",
      "-pix_fmt", "yuv420p",
      "-c:a", "mp2",
      "-b:a", "128k",
      outName,
    ]);
  }

  const data = await ff.readFile(outName);
  const blob = new Blob([data.buffer], { type: "video/mp4" });
  const url = URL.createObjectURL(blob);

  try { await ff.unlink(inName); } catch {}
  try { await ff.unlink(outName); } catch {}

  return { blob, url, mimeType: "video/mp4", filename: "recording.mp4" };
}


export async function ensureMp4(
  { blob, mimeType, filename },
  opts = {}
) {
  const { speedProfile = "veryfast", crf, maxWidth, fps, onProgress } = opts || {};

  const extHint = sniffExt(mimeType, filename);
  const android = isAndroid();
  const ios = isIOS();

  if (extHint === "mp4") {
    const url = URL.createObjectURL(blob);
    return { blob, url, mimeType: "video/mp4", filename: filename || "recording.mp4", converted: false };
  }

  if (android) {
    if (extHint === "webm") {
      return await convertWebMToMp4(blob, { speedProfile, crf, maxWidth, fps, onProgress });
    }
    const sig = await sniffContainerFromBytes(blob);
    if (sig === "webm") {
      return await convertWebMToMp4(blob, { speedProfile, crf, maxWidth, fps, onProgress });
    }
    const url = URL.createObjectURL(blob);
    return { blob, url, mimeType: "video/mp4", filename: filename || "recording.mp4", converted: false };
  }

  if (ios) {
    if (extHint === "webm") {
      return await convertWebMToMp4(blob, { speedProfile, crf, maxWidth, fps, onProgress });
    }
    const sig = await sniffContainerFromBytes(blob);
    if (sig === "webm") {
      return await convertWebMToMp4(blob, { speedProfile, crf, maxWidth, fps, onProgress });
    }
    const url = URL.createObjectURL(blob);
    return { blob, url, mimeType: "video/mp4", filename: filename || "recording.mp4", converted: false };
  }

  const sig = await sniffContainerFromBytes(blob);
  const isMp4 = extHint === "mp4" || sig === "mp4";
  const outMime = isMp4 ? "video/mp4" : (mimeType || "video/webm");
  const outName = isMp4 ? (filename || "recording.mp4") : (filename || "recording.webm");
  const url = URL.createObjectURL(blob);
  return { blob, url, mimeType: outMime, filename: outName, converted: false };
}
